import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { readPendingQuiz } from '../../quiz/pendingQuiz';
import { pendingIntentReturnUrl } from '../../utils/pendingIntent';
import styles from './EnterDob.module.css';
import { ONBOARDING_ACTIONS, ONBOARDING_STEPS } from '@aluvo/constants/src/onboardingDiagnostics';
import { OnboardingDiagnostics } from '../../diagnostics/onboardingDiagnostics';

const DOB = ONBOARDING_STEPS.DOB.key;

export default function EnterDob() {
    const navigate = useNavigate();
    const location = useLocation();
    const { submitDob, user } = useAuth();
    // The DOB page is the step before onboarding for accounts created without a DOB.
    const inOnboarding = !!user?.needsOnboarding;
    const viewedRef = useRef(false);

    useEffect(() => {
        if (!inOnboarding || viewedRef.current) return;
        viewedRef.current = true;
        OnboardingDiagnostics.stepViewed(DOB);
    }, [inOnboarding]);

    const [dateOfBirth, setDateOfBirth] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const calculateAge = (birthDate) => {
        const today = new Date();
        const birth = new Date(birthDate);
        let age = today.getFullYear() - birth.getFullYear();
        const monthDiff = today.getMonth() - birth.getMonth();
        const dayDiff = today.getDate() - birth.getDate();

        if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
            age--;
        }
        return age;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!dateOfBirth) {
            setError('Please enter your date of birth');
            return;
        }

        const age = calculateAge(dateOfBirth);
        if (age < 13) {
            // Blocked by the age rule — not a technical failure, not an attempt.
            if (inOnboarding) OnboardingDiagnostics.stepAction(DOB, ONBOARDING_ACTIONS.CONTINUE_DISABLED, { reason: 'under_minimum_age' });
            setError('You must be at least 13 years old to use Aluvo');
            return;
        }

        if (inOnboarding) OnboardingDiagnostics.stepAction(DOB, ONBOARDING_ACTIONS.CONTINUE);
        setLoading(true);
        const startedAt = Date.now();
        try {
            const result = await submitDob(dateOfBirth);
            // signup → DOB → onboarding (never a generic Home) for new users;
            // otherwise resume the quiz / page that triggered the DOB gate.
            if (result?.needsOnboarding) { navigate('/onboarding', { replace: true }); return; }
            const pending = readPendingQuiz(user?.id);
            if (pending?.path) { navigate(pending.path, { replace: true }); return; }
            const from = location.state?.from;
            const fromPath = from?.pathname ? `${from.pathname}${from.search || ''}${from.hash || ''}` : null;
            // A gated guest action (e.g. a movie rating) resumes on its own page,
            // which replays the still-valid intent.
            navigate(fromPath || pendingIntentReturnUrl() || '/', { replace: true });
        } catch (err) {
            if (inOnboarding) {
                OnboardingDiagnostics.stepError(DOB, { operation: 'submit_dob', endpoint: 'auth_dob', error: err, blocking: true, durationMs: Date.now() - startedAt });
            }
            setError(err.message || 'Failed to save date of birth');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={styles.page}>
            <div className={styles.container}>
                <div className={styles.icon}>🎂</div>
                <h1 className={styles.title}>When's your birthday?</h1>
                <p className={styles.subtitle}>
                    You must be at least 13 years old to use Aluvo.
                </p>
                <p className={styles.privacy}>
                    Your age will be used for compliance purposes and won't be publicly displayed.
                </p>

                <form onSubmit={handleSubmit} className={styles.form}>
                    <input
                        type="date"
                        value={dateOfBirth}
                        onChange={(e) => setDateOfBirth(e.target.value)}
                        max={new Date().toISOString().split('T')[0]}
                        min="1900-01-01"
                        className={styles.dateInput}
                        required
                    />

                    {error && <p className={styles.error}>{error}</p>}

                    <button
                        type="submit"
                        className={styles.submitBtn}
                        disabled={loading}
                    >
                        {loading ? 'Saving...' : 'Continue'}
                    </button>
                </form>
            </div>
        </div>
    );
}